;(function () {
    const POPUP = "quick-add-popup"
    const TRIGGER = "[data-quick-add]"
    const CLOSE = "[data-quick-add-close]"
    const BODY = "[data-quick-add-body]"
    const CONTENT = "#quick-add-content"
    const DRAWER = "product-size-chart-drawer"
    const FABRIC_SWATCH = "[data-quick-add-fabric-swatch]"
    const ANNOUNCER = "[data-quick-add-announcer]"
    const VARIANT_SELECTOR = "product-card-variant-selector"
    const GALLERY = "[data-quick-add-gallery]"
    const MONOGRAM_LINK = "[data-monogram-link]"
    const PRODUCT_CARD = "product-card"
    const SELECTED_VARIANT_ATTR = "data-product-card-selected_variant_id"

    const OPEN_CLASS = "open"

    const LOADING_CLASS = "loading"

    const SWAPPING_CLASS = "is-quick-add-swapping"
    const CONTENT_LOADING_CLASS = "is-quick-add-loading"

    const PREFETCH_DELAY_MS = 65 
    const CACHE_LIMIT = 8 
    const CLOSE_AFTER_ADD_MS = 350 
    const LOADING_DELAY_MS = 250
    const FADE_OUT_MS = 160
    const FADE_IN_MS = 240
    const WARM_IMAGE_COUNT = 3
    const DECODE_TIMEOUT_MS = 500

    const parser = new DOMParser()
    const documentCache = new Map() // handle -> Promise<Document>

    let requestToken = 0
    let prefetchTimer = null
    let prefetchTarget = null

    const wait = (ms) =>
        new Promise((resolve) => window.setTimeout(resolve, ms))

    const prefersReducedMotion = () =>
        window.matchMedia("(prefers-reduced-motion: reduce)").matches

    // ---------- fetching ----------

    function fragmentUrl(handle) {
        const root = window.Shopify?.routes?.root || "/"
        return `${root}products/${handle}?view=quick-add`
    }

    function fetchDocument(handle) {
        const cached = documentCache.get(handle)

        if (cached) {
            documentCache.delete(handle)
            documentCache.set(handle, cached)
            return cached
        }

        const request = fetch(fragmentUrl(handle), {
            credentials: "same-origin",
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Quick add: ${response.status} fetching ${handle}`
                    )
                }
                return response.text()
            })
            .then((html) => parser.parseFromString(html, "text/html"))
            .catch((error) => {
                documentCache.delete(handle) // never cache a failure
                throw error
            })

        if (documentCache.size >= CACHE_LIMIT) {
            documentCache.delete(documentCache.keys().next().value)
        }
        documentCache.set(handle, request)

        return request
    }

    function prefetch(handle) {
        if (!handle || documentCache.has(handle)) return
        
        fetchDocument(handle).catch(() => {})
    }

    function warmImages(doc) {
        const sources = Array.from(
            doc.querySelectorAll(`${GALLERY} img`)
        ).slice(0, WARM_IMAGE_COUNT)
        if (!sources.length) return Promise.resolve()

        const decoded = sources.map((source) => {
            const image = new Image()

            if (source.sizes) image.sizes = source.sizes
            if (source.srcset) image.srcset = source.srcset
            image.src = source.src
            return image.decode().catch(() => {})
        })

        return Promise.race([Promise.all(decoded), wait(DECODE_TIMEOUT_MS)])
    }

    // ---------- element ----------
    class QuickAddPopup extends HTMLElement {
        constructor() {
            super()
            this.onKeydown = this.onKeydown.bind(this)
            this.close = this.close.bind(this)
            this.handle = null
            this.drawer = null
            this.monogramLinkObserver = null
        }

        connectedCallback() {
            this.body = this.querySelector(BODY)

            this.addEventListener("click", (event) => {
                if (event.target.closest(CLOSE)) this.close()
            })

            this.addEventListener("click", (event) => this.onFabricClick(event))

            this.addEventListener("/cart-form:res", () => {
                window.setTimeout(
                    () => this.close({ restoreScroll: false }),
                    CLOSE_AFTER_ADD_MS
                )
            })
        }

        isOpen() {
            return this.classList.contains(OPEN_CLASS)
        }

        // ---------- open / close ----------
        async open(handle) {
            if (!handle) return

            const token = ++requestToken
            const isCurrent = () => token === requestToken

            if (this.handle !== handle) this.body.replaceChildren()

            this.handle = handle
            this.classList.add(OPEN_CLASS, LOADING_CLASS)
            document.body.classList.add("!overflow-hidden")
            document.addEventListener("keydown", this.onKeydown)
            if (window.lenis) window.lenis.stop()

            try {
                const doc = await fetchDocument(handle)
                if (!isCurrent()) return

                await warmImages(doc)
                if (!isCurrent()) return

                this.inject(doc)
            } catch (error) {
                console.error(error)

                window.location.href = `${window.Shopify?.routes?.root || "/"}products/${handle}`
            } finally {
                if (isCurrent()) this.classList.remove(LOADING_CLASS)
            }
        }

        close({ restoreScroll = true } = {}) {
            if (!this.isOpen()) return

            this.classList.remove(OPEN_CLASS, LOADING_CLASS)

            this.closeDrawers()

            this.content()?.classList.remove(
                SWAPPING_CLASS,
                CONTENT_LOADING_CLASS
            )

            document.removeEventListener("keydown", this.onKeydown)

            if (restoreScroll) {
                document.body.classList.remove("!overflow-hidden")
                if (window.lenis) window.lenis.start()
            }

            requestToken++ // orphan any swap still in flight
        }

        onKeydown(event) {
            if (event.key !== "Escape") return

            if (this.drawer?.classList.contains("open")) return
            this.close()
        }

        // ---------- DOM ----------
        keepScrollLocked(drawer) {
            const close = drawer.close.bind(drawer)

            drawer.close = () => {
                close()
                if (!this.isOpen()) return
                document.body.classList.add("!overflow-hidden")
                if (window.lenis) window.lenis.stop()
            }
        }

        syncMonogramLink(content) {
            this.monogramLinkObserver?.disconnect()
            this.monogramLinkObserver = null

            const link = content.querySelector(MONOGRAM_LINK)
            const card = content.querySelector(PRODUCT_CARD)
            if (!link || !card) return

            const base = link.dataset.monogramLink
            if (!base) return

            const apply = () => {
                const id = card.getAttribute(SELECTED_VARIANT_ATTR)
                link.href = id ? `${base}?variant=${id}` : base
            }

            apply()

            this.monogramLinkObserver = new MutationObserver(apply)
            this.monogramLinkObserver.observe(card, {
                attributes: true,
                attributeFilter: [SELECTED_VARIANT_ATTR],
            })
        }

        closeDrawers() {
            if (
                this.drawer &&
                this.drawer.classList.contains("open") &&
                typeof this.drawer.close === "function"
            ) {
                this.drawer.close()
            }
            this.drawer?.remove()
            this.drawer = null

            this.monogramLinkObserver?.disconnect()
            this.monogramLinkObserver = null
        }

        inject(doc, startHidden = false) {
            const incoming = doc.querySelector(CONTENT)
            if (!incoming) {
                throw new Error("Quick add: no content in the response")
            }

            this.closeDrawers()

            const content = incoming.cloneNode(true)
            const drawer = content.querySelector(DRAWER)
            drawer?.remove()

            if (startHidden) content.classList.add(SWAPPING_CLASS)

            this.body.replaceChildren(content)

            if (drawer) {
                document.body.appendChild(drawer)
                this.drawer = drawer
                this.keepScrollLocked(drawer)
            }

            this.syncMonogramLink(content)

            this.handle = content.dataset.quickAddHandle || this.handle

            window.ProductVariantGuard?.suppressDefaultSelection()

            return content
        }

        // ---------- fabric swap ----------
        captureSelectedOptions() {
            const selector = this.querySelector(VARIANT_SELECTOR)
            if (!selector) return []

            return Array.from(selector.querySelectorAll("[data-option-value]"))
                .filter((el) =>
                    el.tagName === "INPUT"
                        ? el.checked
                        : el.classList.contains("selected")
                )
                .map((el) => ({
                    type: el.dataset.optionType,
                    value: el.dataset.optionValue,
                }))
        }

        restoreSelectedOptions(options) {
            if (!options.length) return

            const selector = this.querySelector(VARIANT_SELECTOR)
            if (!selector) return

            options.forEach(({ type, value }) => {
                if (!type || !value) return
                const match = selector.querySelector(
                    `[data-option-value="${CSS.escape(value)}"][data-option-type="${CSS.escape(type)}"]:not([disabled])`
                )
                if (match) match.click()
            })
        }

        setActiveSwatch(handle) {
            this.querySelectorAll(FABRIC_SWATCH).forEach((swatch) =>
                swatch.setAttribute(
                    "aria-current",
                    swatch.dataset.quickAddHandle === handle ? "true" : "false"
                )
            )
        }

        announce(name) {
            const announcer = this.querySelector(ANNOUNCER)
            if (announcer && name) announcer.textContent = name
        }

        content() {
            return this.querySelector(CONTENT)
        }

        runSwap(mutate, isCurrent) {
            if (prefersReducedMotion()) {
                mutate(false)
                return Promise.resolve()
            }

            const outgoing = this.content()
            if (!outgoing) {
                mutate(false)
                return Promise.resolve()
            }

            outgoing.classList.add(SWAPPING_CLASS)

            return wait(FADE_OUT_MS).then(() => {
                if (!isCurrent()) return

                const content = mutate(true) // inserted already faded out
                if (!content) return

                void content.offsetWidth // flush, so the class removal animates
                content.classList.remove(SWAPPING_CLASS)
                return wait(FADE_IN_MS)
            })
        }

        async onFabricClick(event) {
            if (event.defaultPrevented) return
            if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return // let the browser open a new tab as asked
            }

            const swatch = event.target?.closest?.(FABRIC_SWATCH)
            if (!swatch || !this.contains(swatch)) return

            event.preventDefault()
            event.stopPropagation() // never let the PDP's fabric swap see this
            if (swatch.getAttribute("aria-current") === "true") return

            const handle = swatch.dataset.quickAddHandle
            const name = swatch.dataset.quickAddName

            const token = ++requestToken
            const isCurrent = () => token === requestToken

            const options = this.captureSelectedOptions()

            this.setActiveSwatch(handle)

            const loadingTimer = window.setTimeout(() => {
                if (isCurrent()) {
                    this.content()?.classList.add(CONTENT_LOADING_CLASS)
                }
            }, LOADING_DELAY_MS)

            try {
                const doc = await fetchDocument(handle)
                if (!isCurrent()) return

                await warmImages(doc)
                if (!isCurrent()) return

                await this.runSwap((startHidden) => {
                    const content = this.inject(doc, startHidden)
                    this.restoreSelectedOptions(options)
                    this.announce(name)
                    return content
                }, isCurrent)
            } catch (error) {
                console.error(error)
                window.location.href = swatch.href
            } finally {
                window.clearTimeout(loadingTimer)

                if (isCurrent()) {
                    this.content()?.classList.remove(CONTENT_LOADING_CLASS)
                }
            }
        }
    }

    // ---------- triggers ----------
    function getPopup() {
        return document.querySelector(POPUP)
    }

    function triggerCandidate(event) {
        const trigger = event.target?.closest?.(TRIGGER)

        if (!trigger || trigger.closest(POPUP)) return null
        return trigger
    }

    function onClick(event) {
        const trigger = triggerCandidate(event)
        if (!trigger) return

        event.preventDefault()

        window.clearTimeout(prefetchTimer)
        prefetchTarget = null

        getPopup()?.open(trigger.dataset.quickAdd)
    }

    function onPrefetchIntent(event) {
        const trigger = triggerCandidate(event)
        if (!trigger || trigger === prefetchTarget) return

        prefetchTarget = trigger
        window.clearTimeout(prefetchTimer)
        prefetchTimer = window.setTimeout(
            () => prefetch(trigger.dataset.quickAdd),
            PREFETCH_DELAY_MS
        )
    }

    function onTouchIntent(event) {
        const trigger = triggerCandidate(event)
        if (trigger) prefetch(trigger.dataset.quickAdd)
    }

    function init() {
        if (!customElements.get(POPUP)) {
            customElements.define(POPUP, QuickAddPopup)
        }

        document.addEventListener("click", onClick)
        document.addEventListener("pointerover", onPrefetchIntent)
        document.addEventListener("focusin", onPrefetchIntent)
        document.addEventListener("touchstart", onTouchIntent, {
            passive: true,
        })
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init)
    } else {
        init()
    }
})()
